//
// pusher.ts
//
// Copyright 2024-Present Couchbase, Inc.
//
// Use of this software is governed by the Business Source License included
// in the file licenses/BSL-Couchbase.txt.  As of the Change Date specified
// in that file, in accordance with the Business Source License, use of this
// software will be governed by the Apache License, Version 2.0, included in
// the file licenses/APL2.txt.
//

// github.com/couchbase/couchbase-lite-core/blob/master/modules/docs/pages/replication-protocol.adoc

import {Endpoint, type EndpointConfig, type EndpointParams} from "./endpoint";
import type { LocalSequence, PushRevision, ReplicationFilter } from "./types";
import { ReplicatorError } from "./types";
import type * as blip from "@/blip/blip";
import type { JSONArray } from "@/util/json_types";
import { WithShadowAttachments } from "@/blob/sync_blobs";
import { assert } from "@/util/assert";
import { getGeneration } from "@/database/internals";


const kDefaultBatchSize = 200;


export interface PusherConfig extends EndpointConfig {
    filter?     : ReplicationFilter;
    batchSize?  : number;       // Number of changes to get from local db at once [default 200]
}

export interface PusherDelegate {
    /** Fetches up to `limit` revisions starting after sequence `seq`, ordered by sequence */
    getChanges(since: LocalSequence | undefined, limit: number): Promise<ChangeSet>;

    getBlob(digest: string): Promise<Uint8Array | undefined>;

    /** Notification that a revision has been pushed, or failed to be pushed. */
    pushedRev?(rev: PushRevision, error?: Error): void;
}


export interface ChangeSet {
    changes: Array<PushRevision>,   // Revisions
    since?: LocalSequence,          // The `since` value passed to `getChanges`
    lastSequence?: LocalSequence,   // The highest sequence available
}


export class Pusher extends Endpoint {

    constructor(params: EndpointParams,
                private readonly config: PusherConfig,
                private readonly delegate: PusherDelegate)
    {
        super(params, config, "push");

        this.#lastSeqRead = this.checkpointer.localSequence;
        this.#seqTracker = new LocalSeqTracker(this.#lastSeqRead);

        void this.sendMoreChanges();
    }

    override checkIdle() : boolean {
        assert(this.#pendingChangesReplies >= 0 && this.#pendingRevsToSend >= 0);
        return this.caughtUp
            && this.#pendingChangesReplies === 0 && this.#pendingRevsToSend === 0;
    }


    private async getMoreChanges() : Promise<ChangeSet> {
        const limit = this.config.batchSize ?? kDefaultBatchSize;
        const since = this.#lastSeqRead;
        this.logger.debug `Getting changes since seq ${since}`;
        const changeSet = await this.delegate.getChanges(since, limit);
        if (changeSet.lastSequence)
            this.#lastSeqRead = changeSet.lastSequence;
        return changeSet;
    }


    /** This is an async task that runs until the replication completes. */
    private async sendMoreChanges() {
        let pendingSaves: Promise<void>[] | undefined;
        while (true) {
            const changeSet = await this.getMoreChanges();
            let lastSeq = this.#seqTracker.max;
            const n = changeSet.changes.length;
            if (n === 0) {
                if (!this.caughtUp) {
                    this.logger.info `Done proposing existing changes, at sequence ${lastSeq}`;
                    this.caughtUp = true;
                    this.statusChanged();
                    // Wait for the previous round of revs to finish sending:
                    if (pendingSaves) {
                        await Promise.all(pendingSaves);
                        pendingSaves = undefined;
                    }
                    void this.checkpointer.saveNow();
                    this.statusChanged();
                } else if (this.config.continuous) {
                    this.logger.debug `No new changes`;
                }
                if (this.config.continuous)
                    continue;
                else
                    return;
            }

            const body = changeSet.changes.map( change => {
                const a: JSONArray = [change.id, change.rev, change.serverRev ?? ""];
                if (change.deleted)
                    a.push(true);
                this.#seqTracker.addSequence(change.seq);
                lastSeq = change.seq;
                return a;
            });

            this.logger.debug `Proposing ${n} revs from seq ${changeSet.changes[0].seq} -- ${lastSeq}`;
            ++this.#pendingChangesReplies;
            this.statusChanged();
            const reply = await this.socket.send({
                Profile:    "proposeChanges",
                collection: this.collectionIndex
            }, body, "nothrow");
            --this.#pendingChangesReplies;
            this.statusChanged();

            // Wait for the previous round of revs to finish sending:
            if (pendingSaves)
                await Promise.all(pendingSaves);

            if (!this.socketOpen)
                return;

            if (reply.isError) {
                this.replicator.fatalError(reply.error!);
                return;
            } else {
                pendingSaves = new Array<Promise<void>>();
                const wanted = reply.bodyJSON as JSONArray;
                let i = 0;
                for (const rev of changeSet.changes) {
                    const status = (i < wanted.length) ? wanted[i] : 0;
                    switch (status) {
                        case 0:
                            pendingSaves.push(this.sendRev(rev));
                            break;
                        case 304:
                            this.#seqTracker.finishedSequence(rev.seq);
                            break;
                        case 409:
                        default:
                            this.logger.error `Server rejected rev ${rev.id} ${rev.rev} with status ${JSON.stringify(status)}`;
                            this.#seqTracker.finishedSequence(rev.seq);
                            break;
                    }
                    ++i;
                }
                this.logger.debug `Server wants ${pendingSaves.length} of ${n} revs`;
                this.checkpointer.localSequence = this.#seqTracker.checkpointedSequence;
                this.statusChanged();
            }
        }
    }


    /** Sends a 'rev' message with a single document revision. */
    private async sendRev(rev: PushRevision) {
        ++this.#pendingRevsToSend;
        this.statusChanged();
        this.logger.debug `Sending 'rev' message for ${rev.id} ${rev.rev}`;
        const props: blip.Properties = {
            Profile:    "rev",
            collection: this.collectionIndex,
            id:         rev.id,
            rev:        rev.rev
        };
        if (rev.deleted)
            props.deleted = 1;

        // We don't store the doc's rev tree. Just make up an intervening rev history:
        const history: string[] = [];
        const serverGen = rev.serverRev ? getGeneration(rev.serverRev) : 0;
        for (let gen = getGeneration(rev.rev) - 1; gen > serverGen; --gen)
            history.push(`${gen}-faded0001234567812345678`);
        if (rev.serverRev)
            history.push(rev.serverRev);
        if (history.length > 0)
            props.history = history.join(",");

        const body = JSON.stringify(WithShadowAttachments(rev.body, rev.rev));

        const reply = await this.socket.send(props, body, "nothrow");
        this.statusChanged();

        const error = reply.error;
        let completed = true;
        if (error) {
            this.logger.error `Got error response to "rev" message ${rev.id} ${rev.rev} (seq ${rev.seq}: ${error}`;
            //TODO: Identify transient errors; if so, reschedule the rev
            if (error.blipErrorCode >= 500)
                completed = false;
        }

        if (completed) {
            this.#seqTracker.finishedSequence(rev.seq);
            this.checkpointer.localSequence = this.#seqTracker.checkpointedSequence;
            this._progress++;
            this.statusChanged();
            this.delegate.pushedRev?.(rev, error);
        }
        --this.#pendingRevsToSend;
        this.statusChanged();
    }


    // Server is asking for a blob's contents. This happens while I'm uploading a doc, and the
    // server doesn't have a blob matching a digest.
    async onGetAttachment(msg: blip.Message) {
        const digest = msg.property("digest");
        this.logger.debug `Sending blob ${digest}`;
        const contents = await this.delegate.getBlob(digest);
        if (contents)
            this.socket.sendReplyTo(msg, {}, contents);
        else
            this.socket.sendErrorReplyTo(msg, "Unknown blob", 404);
    }


    toString(): string { return `Pusher[${this.collectionID}]`; }


    #seqTracker!            : LocalSeqTracker;
    #lastSeqRead?           : LocalSequence;
    #pendingChangesReplies  = 0;
    #pendingRevsToSend      = 0;
}


/** Keeps track of which remote sequences are pending or completed, and what sequence to use as
 *  the checkpoint. */
export class LocalSeqTracker {
    /// Constructor takes the latest checkpointed sequence.
    constructor(since: LocalSequence | undefined) {
        this.#latest  = since ?? (0 as LocalSequence);
    }

    /// Adds a new pending sequence. Sequences are assumed to be in chronological order.
    addSequence(seq: LocalSequence) {
        if (seq <= this.#latest)
            throw new ReplicatorError(`LocalSeqTracker.addSequence: sequence ${seq} out of order`, 500);
        this.#requested.set(seq, this.#latest);
        this.#latest = seq;
    }

    /// Records that a pending sequence is now complete.
    finishedSequence(seq: LocalSequence) {
        if (!this.#requested.delete(seq))
            throw new ReplicatorError(`LocalSeqTracker.finishedSequence: ${seq} was not pending`, 500);
    }

    get max() : LocalSequence {return this.#latest;}

    get checkpointedSequence() : LocalSequence {
        const first = this.#requested.values().next();
        return first.done ? this.#latest : first.value;
    }


    #requested  = new Map<LocalSequence,LocalSequence>();
    #latest     : LocalSequence;
}
